import { useState } from 'react';
import { useFlags } from 'launchdarkly-react-client-sdk';

import useAuthenticationStatus from 'legacy/common/hooks/useAuthenticationStatus';
import computeCheckoutSessionUpdate from '../helpers/computeCheckoutSessionUpdate';
import { logEvent } from 'legacy/common/utils/amplitude';
import { EVENT_REDIRECT_ON_CHECKOUT_SESSION_COMPLETED_ERROR } from 'legacy/common/constants/events';
import useParams from 'legacy/common/hooks/useParams';
import {
  useConfirmMultiTierCheckoutSession,
  useStartMultiTierCheckoutSession,
  useStartUnauthenticatedMultiTierCheckoutSession,
  useUpdateMultiTierCheckoutSession,
  useUpdateUnauthenticatedMultiTierCheckoutSession
} from '../../../../hooks';
import {
  ConfirmCheckoutSessionParams,
  DiscountCodeDto,
  EventForUser,
  MultiTierCheckoutSession,
  StartCheckoutFn,
  UpdateCheckoutFn
} from '../../../../types';
import logTransactionToGA from '../helpers/logTransactionToGA';
import { FLAGS } from 'legacy/common/constants/launchdarkly';
import {
  useStartMultiTierCheckoutSession as useStartMultiTierCheckoutSessionV3,
  useUpdateMultiTierCheckoutSession as useUpdateMultiTierCheckoutSessionV3,
  useConfirmMultiTierCheckoutSession as useConfirmMultiTierCheckoutSessionV3,
  useStartUnauthenticatedMultiTierCheckoutSession as useStartUnauthenticatedMultiTierCheckoutSessionV3,
  useUpdateUnauthenticatedMultiTierCheckoutSession as useUpdateUnauthenticatedMultiTierCheckoutSessionV3
} from 'api/queries/events/checkout';
import usePaymentMethod from 'hooks/usePaymentMethod';
import computeCheckoutSessionUpdateV3 from '../helpers/computeCheckoutSessionUpdateV3';
import { ConfirmCheckoutSessionParams as ConfirmCheckoutSessionParamsV3 } from 'api/types/events/checkout';

const useMultiTierCheckoutSession = (
  event: EventForUser,
  discountData: DiscountCodeDto
) => {
  const flags = useFlags();
  const isNewCheckoutEnabled = flags[FLAGS.ENABLE_EVENT_CHECKOUT_V3];
  const params = useParams<{ t?: string }>();
  const { isAuthenticated } = useAuthenticationStatus();
  const { getCardStripePaymentMethodIdFromCardId } = usePaymentMethod();
  const [checkoutSession, setCheckoutSession] =
    useState<MultiTierCheckoutSession>();

  /**
   * Switch the checkout endpoints based on the ENABLE_EVENT_CHECKOUT_V3 feature flag.
   * Once checkout v3 is fully launched, we can remove the old checkout hooks.
   *
   * Sorry for the `any` here, but it's a necessary evil to get the types to work with the conditionals.
   * Will be fixed in the future when removing the legacy checkout hooks.
   */
  const startCheckoutSession = isNewCheckoutEnabled
    ? useStartMultiTierCheckoutSessionV3()
    : useStartMultiTierCheckoutSession();
  const startUnauthenticatedCheckoutSession = isNewCheckoutEnabled
    ? useStartUnauthenticatedMultiTierCheckoutSessionV3()
    : useStartUnauthenticatedMultiTierCheckoutSession();
  const updateCheckoutSession: any = isNewCheckoutEnabled
    ? useUpdateMultiTierCheckoutSessionV3()
    : useUpdateMultiTierCheckoutSession();
  const updateUnauthenticatedCheckoutSession = isNewCheckoutEnabled
    ? useUpdateUnauthenticatedMultiTierCheckoutSessionV3()
    : useUpdateUnauthenticatedMultiTierCheckoutSession();
  const confirmCheckoutSession: any = isNewCheckoutEnabled
    ? useConfirmMultiTierCheckoutSessionV3()
    : useConfirmMultiTierCheckoutSession();

  /**
   * Starts the checkout process for a given event.
   */
  const startCheckout: StartCheckoutFn = (eventId) => {
    if (!eventId) {
      return;
    }

    const startCheckoutMutation = isAuthenticated()
      ? startCheckoutSession
      : startUnauthenticatedCheckoutSession;

    startCheckoutMutation.mutate(eventId, {
      onSuccess: (data) => {
        setCheckoutSession(data as MultiTierCheckoutSession);
      }
    });
  };

  /**
   * Update the checkout session with given parameters and options.
   */
  const updateCheckout: UpdateCheckoutFn = (updateData, cards) =>
    new Promise((resolve, reject) => {
      if (!checkoutSession?.paymentIntentId || !event?._id) {
        reject(
          new Error('paymentIntentId not provided or event id not provided')
        );
      }

      const isUserAuthenticated = isAuthenticated();

      const updateCheckoutMutation = isUserAuthenticated
        ? updateCheckoutSession
        : updateUnauthenticatedCheckoutSession;

      startCheckoutSession.reset();
      confirmCheckoutSession.reset();

      updateCheckoutMutation.mutate(
        {
          eventId: event._id,
          paymentIntentId: checkoutSession?.paymentIntentId,
          body: isNewCheckoutEnabled
            ? computeCheckoutSessionUpdateV3(
                {
                  ...updateData,
                  promoterCode: updateData.promoterCode,
                  trackingId: params.t,
                  stripePaymentMethodType: updateData.stripePaymentMethodType,
                  stripePaymentMethodId: getCardStripePaymentMethodIdFromCardId(
                    updateData.card,
                    cards
                  )
                },
                isUserAuthenticated
              )
            : computeCheckoutSessionUpdate(updateData, isUserAuthenticated)
        },
        {
          onSuccess: (data) => {
            const newCheckoutSessionData = {
              ...data
            };

            if (isNewCheckoutEnabled) {
              newCheckoutSessionData.card = updateData.card;
            }

            setCheckoutSession(newCheckoutSessionData);
            resolve(newCheckoutSessionData);
          },
          onError: (error) => {
            reject(error);
          }
        }
      );
    });

  /**
   * Confirms the checkout session with the provided data.
   * Also logs the event in Google Analytics if transaction was successful.
   */
  const confirmCheckout = (confirmData: { allWaiversAccepted: boolean }) =>
    new Promise((resolve, reject) => {
      if (!checkoutSession?.paymentIntentId || !event?._id) {
        reject(
          new Error('paymentIntentId not provided or event id not provided')
        );
      }

      startCheckoutSession.reset();
      updateCheckoutSession.reset();

      const mutationParams = {
        eventId: event._id,
        paymentIntentId: checkoutSession.paymentIntentId
      } as ConfirmCheckoutSessionParamsV3 & ConfirmCheckoutSessionParams;

      if (isNewCheckoutEnabled) {
        mutationParams.body = {
          isWaiverAccepted: confirmData?.allWaiversAccepted
        };
      } else {
        mutationParams.isWaiverAccepted = confirmData?.allWaiversAccepted;
        mutationParams.promoterCode = discountData?.code || undefined;
        mutationParams.trackingId = params.t || undefined;
      }

      confirmCheckoutSession.mutate(mutationParams, {
        onSuccess: (data) => {
          // Add transaction to google analytics
          logTransactionToGA(checkoutSession, event, discountData);

          if (data?.redirectUrl) {
            // Create an empty document to prevent any unsaved changes dialogs
            document.open();
            document.write(
              `<html><head><meta http-equiv="refresh" content="0; url=${data.redirectUrl}"></head><body>Redirecting...</body></html>`
            );
            document.close();
          } else {
            resolve(null);
          }
        },
        onError: (err) => {
          if (err.code === 'checkoutSessionCompleted') {
            logEvent(EVENT_REDIRECT_ON_CHECKOUT_SESSION_COMPLETED_ERROR);
            return resolve(null);
          }

          return reject(err);
        }
      });
    });

  return {
    isNewCheckoutEnabled,
    checkoutSession,
    isLoadingCheckout:
      startCheckoutSession.isLoading ||
      startUnauthenticatedCheckoutSession.isLoading ||
      updateCheckoutSession.isLoading ||
      updateUnauthenticatedCheckoutSession.isLoading ||
      confirmCheckoutSession.isLoading,
    isStartingCheckout:
      startCheckoutSession.isLoading ||
      startUnauthenticatedCheckoutSession.isLoading,
    isUpdatingCheckout:
      updateCheckoutSession.isLoading ||
      updateUnauthenticatedCheckoutSession.isLoading,
    isConfirmingCheckout: confirmCheckoutSession.isLoading,
    confirmCheckoutError: confirmCheckoutSession.error,
    checkoutError:
      startCheckoutSession.error ||
      updateCheckoutSession.error ||
      startUnauthenticatedCheckoutSession.error ||
      updateUnauthenticatedCheckoutSession.error ||
      confirmCheckoutSession.error,
    isCheckoutSuccess: confirmCheckoutSession.isSuccess,
    isCheckoutDisabled: Boolean(startCheckoutSession.error),
    startCheckout,
    updateCheckout,
    confirmCheckout
  };
};

export default useMultiTierCheckoutSession;
